import { useSelector } from 'react-redux';
import type { AnyAction } from 'redux';
import { createSelector } from 'reselect';
import { call, put, select, takeLatest } from 'typed-redux-saga';

import {
  HYDRATE,
  type HydrateAction,
} from '@/domains/core/applicationState/store/hydrateAction';
import { waitForAndGetFlagValuesSaga } from '@/domains/core/flags/sagas/waitForAndGetFlagValues.saga';
import logger from '@/domains/core/observability/logger';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import { DeliveryOption } from '@/domains/productDelivery/tracking/delivery/trackDeliveryPage.saga';
import type { SpartacuxProductDiscoveryDomainState } from '@/domains/productDiscovery/productDiscovery.model';
import {
  CHANGE_QUANTITY,
  quantitySelector,
} from '@/domains/productDiscovery/ProductPage//common/modules/QuantitySelector/Quantity.ducks';
import type { Detail } from '@/domains/productDiscovery/ProductPage/b2c/interfaces/Detail.model';
import type { DeliveryPrice } from '@/domains/productDiscovery/ProductPage/interfaces/Detail.common.model';
import { FETCH_DELIVERY_OFFERS_FROM_GRAPHQL } from '@/productDelivery/constants/FeatureFlags';
import { ProDetail } from '@/productDiscovery/ProductPage/b2b/interfaces/ProDetail.model';
import { deliveryOfferV2Gql } from '@/productDiscovery/ProductPage/common/modules/Delivery/services/graphql/deliveryOfferV2/deliveryOfferV2Gql';
import { postProductsDeliveryEstimate } from '@/productDiscovery/ProductPage/common/modules/Delivery/services/rest/postProductsDeliveryEstimate';
import {
  PRODUCT_PAGE_INIT,
  type GetDetailSuccess,
} from '@/productDiscovery/ProductPage/detail.actions';
import { detailSelector } from '@/productDiscovery/ProductPage/selectors/detail';

// TYPES
export interface DeliveryState {
  offers: DeliveryOffer[];
  fetching: boolean;
  fetched: boolean;
}

export interface DeliveryOffer {
  countries: string[];
  name: string;
  type: string;
  minDeliveryTime: number;
  maxDeliveryTime: number;
  orderBefore: number | null;
  deliveryPrice: DeliveryPrice;
  isExpress: boolean;
  isBestDeliveryOffer: boolean;
  expressMode?: string;
}

export interface DeliveryMeta {
  fastestMaxDelivery: number | null;
  hasExpressDelivery: boolean;
  hasFreeDelivery: boolean;
  countries: string[];
}

export interface DeliveryQueryParams {
  isSellable: boolean;
  productId: number;
  sellerId: number;
  sku: string;
  quantity: number;
  isManoFulfillment?: boolean;
  unitPrice: {
    amount: number;
    currency: string;
  };
  discounted: boolean;
  weight: number;
}

// INITIAL STATE
const initialState: DeliveryState = {
  offers: [],
  fetching: false,
  fetched: false,
};

// ACTIONS
export const GET_DELIVERY_CARRIER_TRIGGER =
  'product/v2/GET_DELIVERY_CARRIER_TRIGGER';

export interface GetDeliveryCarrierTrigger extends AnyAction {
  type: typeof GET_DELIVERY_CARRIER_TRIGGER;
  queryParams: DeliveryQueryParams;
}

export const GET_DELIVERY_CARRIER = 'product/v2/GET_DELIVERY_CARRIER';

export interface GetDeliveryCarrier extends AnyAction {
  type: typeof GET_DELIVERY_CARRIER;
}

export const GET_DELIVERY_CARRIER_SUCCESS =
  'product/v2/GET_DELIVERY_CARRIER_SUCCESS';

export interface GetDeliveryCarrierSuccess extends AnyAction {
  type: typeof GET_DELIVERY_CARRIER_SUCCESS;
  offers: DeliveryOffer[];
}

export const GET_DELIVERY_CARRIER_FAILURE =
  'product/v2/GET_DELIVERY_CARRIER_FAILURE';

export interface GetDeliveryCarrierFailure extends AnyAction {
  type: typeof GET_DELIVERY_CARRIER_FAILURE;
}

// SELECTORS
export const deliveryStateSelector = (
  state: SpartacuxProductDiscoveryDomainState<'b2c'>,
) => state.productDiscovery?.productPage?.delivery || initialState;

export const useDeliveryState = () => useSelector(deliveryStateSelector);

export const deliveryQuerySelector = createSelector(
  detailSelector,
  quantitySelector,
  (
    detail: Detail | ProDetail | undefined,
    quantity: number,
  ): DeliveryQueryParams => ({
    isSellable: detail?.isSellable || false,
    isManoFulfillment: Boolean(detail?.delivery?.fulfillment),
    weight: Number(detail?.delivery?.weight),
    productId: Number(detail?.productId),
    sellerId: Number(detail?.seller?.id),
    sku: String(detail?.offerSKU),
    unitPrice: {
      amount: detail?.price?.primaryPrice.amountWithVat || 0,
      currency: detail?.currency || 'EUR',
    },
    discounted: false,
    quantity: Number(quantity),
  }),
);

export const deliveryMetaSelector = createSelector(
  deliveryStateSelector,
  (state): DeliveryMeta => {
    return {
      fastestMaxDelivery: Math.min(
        ...state.offers.map((offer) => offer.maxDeliveryTime),
      ),
      hasExpressDelivery: state.offers.some((offer) => offer.isExpress),
      hasFreeDelivery: state.offers.some(
        (offer) => offer.deliveryPrice.amount === 0,
      ),
      countries: state.offers.flatMap((offer) => offer.countries),
    };
  },
);

export const useDeliveryMeta = () => useSelector(deliveryMetaSelector);

// REDUCER
export default function reducer(
  state: DeliveryState = initialState,
  action:
    | GetDetailSuccess<'b2b'>
    | HydrateAction<SpartacuxProductDiscoveryDomainState>
    | GetDeliveryCarrier
    | GetDeliveryCarrierSuccess
    | GetDeliveryCarrierFailure,
): DeliveryState {
  switch (action.type) {
    case HYDRATE: {
      return initialState;
    }

    case GET_DELIVERY_CARRIER: {
      return { ...state, fetching: true, fetched: false };
    }

    case GET_DELIVERY_CARRIER_SUCCESS: {
      const offers = action.offers;

      return {
        fetching: false,
        fetched: true,
        offers,
      };
    }

    case GET_DELIVERY_CARRIER_FAILURE: {
      return { ...initialState, fetching: false, fetched: true };
    }

    default: {
      return state;
    }
  }
}

// SAGA
export function* fetchDeliverySaga() {
  const queryParams = yield* select(deliveryQuerySelector);

  try {
    if (!queryParams.isSellable) return;

    yield* put({ type: GET_DELIVERY_CARRIER });

    const flags = yield* call(waitForAndGetFlagValuesSaga);
    const isFetchDeliveryOffersFromGraphQlEnabled =
      flags[FETCH_DELIVERY_OFFERS_FROM_GRAPHQL];

    const deliveryOffers: DeliveryOffer[] =
      yield* isFetchDeliveryOffersFromGraphQlEnabled
        ? call(deliveryOfferV2Gql, queryParams)
        : call(postProductsDeliveryEstimate, queryParams);

    const hasFreeShipping =
      deliveryOffers?.some(({ deliveryPrice }) => deliveryPrice.amount === 0) ||
      false;

    const deliveryOptions: DeliveryOption[] | undefined = deliveryOffers?.map(
      ({ type, deliveryPrice }) => ({
        delivery_mode: type,
        has_free_shipping: hasFreeShipping,
        cost: deliveryPrice.amount,
      }),
    );

    Gtm.push({
      event: 'interaction_display delivery informations',
      event_trigger: 'product_page',
      amp_event_name: 'Display delivery informations',
      page_type: 'product',
      delivery_options: deliveryOptions,
    });

    yield* put({ type: GET_DELIVERY_CARRIER_SUCCESS, offers: deliveryOffers });
  } catch (error) {
    yield* put({ type: GET_DELIVERY_CARRIER_FAILURE });
    logger.error(error);

    if (error.code === 400) {
      logger.error(
        new Error('delivery estimate 400', { queryParams, ...error }),
      );
    }
  }
}

// SAGA TRIGGERS
export function* rootDeliverySaga() {
  yield* takeLatest(PRODUCT_PAGE_INIT, fetchDeliverySaga); // this will trigger on SPA navigation -> this event was used just to have something reliable - implement own event
  yield* takeLatest(CHANGE_QUANTITY, fetchDeliverySaga);
}
